import React, { useReducer, useEffect, useRef, useState } from 'react';
import { IBasketItem, IAction } from '../../pages/checkout/[slug]';
import { Button, Grid, Container, makeStyles, Hidden, Typography, Box } from '@material-ui/core';
import produce from 'immer';
import ProductTable from './ProductTable';
import BillingDetails, { IDiscountCodeApplied } from './BillingDetails';
import { IPlainProduct, Currency } from '../../backend/types/generic';
import { DiscountCode } from './DiscountCode';
import { TermsAndConditions } from '../Misc/TermsAndConditions';
import { ICheckoutResult } from '../../backend/types/responses';

export const QUANTITY_LIMIT = 1000;

const useStyles = makeStyles((theme) => ({
  checkoutButton: {
    height: 36,
  },
  heading: {
    paddingBottom: '1em',
    paddingTop: '1em',
  },
  checkoutBar: {
    marginTop: 12,
  },
  billing: {
    order: 2,
    [theme.breakpoints.up('md')]: {
      order: 1,
    },
  },
  shipping: {
    order: 1,
    [theme.breakpoints.up('md')]: {
      order: 2,
    },
  },
}));

interface IState {
  items: IBasketItem[];
  discountCode: string;
  // currency: Currency;
  discountCodeApplied?: IDiscountCodeApplied;
  notFound?: boolean;
}

const initialState: IState = {
  items: [],
  discountCode: '',
};

const reducer = (state: IState, action: IAction) => {
  switch (action.type) {
    case 'SET_DISCOUNT_CODE':
      return produce(state, (draft) => {
        draft.discountCode = action.code;
      });
    case 'SET_QUANTITY':
      const newQuantity = Number(action.quantity);
      if (newQuantity <= 0 || newQuantity > QUANTITY_LIMIT || Number.isNaN(newQuantity)) {
        return state;
      }
      return produce(state, (draft) => {
        draft.items[action.index].quantity = newQuantity;
      });
    case 'REMOVE_QUANTITY':
      return produce(state, (draft) => {
        const quantity = draft.items[action.index].quantity;
        if (quantity > 1) {
          draft.items[action.index].quantity -= 1;
        }
        // // TODO: In the future, we want a dialog prompt when products will be removable from basket
        // } else {
        //   draft.items.splice(action.index, 1);
        // }
      });

    case 'ADD_QUANTITY':
      return produce(state, (draft) => {
        draft.items[action.index].quantity += 1;
      });

    case 'ADD_TO_BASKET':
      return produce(state, (draft) => {
        draft.items.push({ ...action.product, quantity: 1 });
      });
    case 'APPLY_DISCOUNT_CODE':
      return produce(state, (draft) => {
        draft.discountCodeApplied = action.discountCodeApplied;
      });
    case 'CLEAR_DISCOUNT_CODE':
      return produce(state, (draft) => {
        draft.discountCodeApplied = null;
        draft.discountCode = '';
      });
    default:
      return initialState;
  }
};

interface IProps {
  product?: IPlainProduct;
  loading: boolean;
  id: string;
  onCheckoutCompleted(response: ICheckoutResult): void;
}

export const ShoppingBasket: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { product, loading } = props;
  const { items, discountCode, discountCodeApplied } = state;

  const styles = useStyles({});
  const formikRef = useRef(null);

  const [termsOpen, setTermsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (product) {
      dispatch({ type: 'ADD_TO_BASKET', product });
    }
  }, [product]);

  return (
    <Container maxWidth="lg">
      <TermsAndConditions open={termsOpen} handleClose={() => setTermsOpen(false)} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={5} className={styles.billing}>
          <BillingDetails
            onCheckoutCompleted={props.onCheckoutCompleted}
            reqId={props.id}
            ref={formikRef}
            discountCode={discountCode}
            items={items}
          />
        </Grid>
        <Grid item xs={12} md={7} className={styles.shipping}>
          <ProductTable
            dispatch={dispatch}
            items={items}
            loading={loading}
            discountCodeApplied={discountCodeApplied}
          />
          <Grid container className={styles.checkoutBar} alignItems="center">
            {/* <Grid item xs={12}>
              <DiscountCode
                dispatch={dispatch}
                discountCode={state.discountCode}
                discountCodeApplied={discountCodeApplied}
                items={items}
              />
            </Grid> */}

            <Hidden smDown>
              <Grid item xs={7}>
                <Typography variant="body2">
                  By clicking Checkout you agree to our{' '}
                  <a href="#" onClick={() => setTermsOpen(true)}>
                    <u>Terms and Conditions</u>
                  </a>
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  style={{ marginTop: 12, marginBottom: 12 }}
                  onClick={() => {
                    if (formikRef.current) {
                      formikRef.current.submitForm();
                    }
                  }}
                  className={styles.checkoutButton}
                >
                  Checkout
                </Button>
              </Grid>
            </Hidden>
          </Grid>
        </Grid>
      </Grid>
      <Hidden mdUp>
        <Grid container>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            style={{ marginTop: 12, marginBottom: 12 }}
            onClick={() => {
              if (formikRef.current) {
                formikRef.current.submitForm();
              }
            }}
            className={styles.checkoutButton}
          >
            Checkout
          </Button>
        </Grid>
        <Box px={2} pt={1} pb={2}>
          <Typography variant="body2">
            By clicking Checkout you agree to our{' '}
            <a href="#" onClick={() => setTermsOpen(true)}>
              <u>Terms and Conditions</u>
            </a>
          </Typography>
        </Box>
      </Hidden>
    </Container>
  );
};
